import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';

const Login = () => {
 useDocumentMetadata(
 'Login — Dakhla',
 'Log in to your Dakhla account to track university applications, deadlines, and entrance tests.'
 );

 const navigate = useNavigate();
 const { login } = useAuth();

 const [formData, setFormData] = useState({
 role: 'student',
 email: '',
 password: ''
 });
 const [showPassword, setShowPassword] = useState(false);
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState('');

 const handleChange = (e) => {
 setFormData({
 ...formData,
 [e.target.name]: e.target.value
 });
 if (error) setError('');
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 setLoading(true);
 setError('');

 try {
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 const endpoint = formData.role === 'student'
 ? `${apiUrl}/student/login`
 : `${apiUrl}/university/login`;

 const response = await fetch(endpoint, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 },
 body: JSON.stringify({
 email: formData.email,
 password: formData.password
 }),
 });

 const data = await response.json();

 if (response.ok) {
 login(data.user, data.access_token);
 if (data.user?.role === 'university') {
 navigate('/dashboard');
 } else {
 navigate('/');
 }
 } else {
 setError(data.error || 'Login failed');
 }
 } catch (error) {
 console.error('Login error:', error);
 setError('Network error. Please check if the server is running.');
 } finally {
 setLoading(false);
 }
 };

 return (
 <div className="bg-slate-50 relative flex items-center justify-center py-8 sm:px-6 lg:px-8" style={{ minHeight: 'calc(100vh - var(--navbar-height, 64px))' }}>
 {/* Background decoration */}
 <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
 <div className="absolute top-0 left-0 w-full h-full opacity-[0.025] bg-[radial-gradient(#4f46e5_1px,transparent_1px)] [background-size:24px_24px]" />
 <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-primary-500/8 blur-3xl animate-pulse-ring" style={{ animationDuration: '9s' }} />
 <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-indigo-500/8 blur-3xl animate-pulse-ring" style={{ animationDuration: '12s' }} />
 </div>

 <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10 px-4 sm:px-0">
 {/* Header */}
 <div className="flex flex-col items-center mb-6">
 <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">
 Welcome back
 </h1>
 <p className="text-sm text-slate-500 mt-1">
 Don't have an account?{' '}
 <Link to="/signup" className="font-semibold text-primary-600 hover:text-primary-500 transition-colors">
 Sign up
 </Link>
 </p>
 </div>

 {/* Card */}
 <div className="card relative overflow-hidden bg-white py-9 px-5 sm:rounded-2xl sm:px-10 border border-slate-200 shadow-lg shadow-slate-100">
 {/* Accent line */}

 {/* Role switcher */}
 <div className="mb-5">
 <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
 <button
 type="button"
 id="role-student"
 onClick={() => setFormData({ ...formData, role: 'student' })}
 className={`flex-1 py-2 px-3 text-xs font-semibold rounded-lg transition-all duration-200 ${
 formData.role === 'student'
 ? 'bg-white text-primary-700 shadow-sm border border-slate-200'
 : 'text-slate-500 hover:text-slate-700'
 }`}
 >
 Student
 </button>
 <button
 type="button"
 id="role-university"
 onClick={() => setFormData({ ...formData, role: 'university' })}
 className={`flex-1 py-2 px-3 text-xs font-semibold rounded-lg transition-all duration-200 ${
 formData.role === 'university'
 ? 'bg-white text-primary-700 shadow-sm border border-slate-200'
 : 'text-slate-500 hover:text-slate-700'
 }`}
 >
 University
 </button>
 </div>
 </div>

 <form className="space-y-4" onSubmit={handleSubmit}>
 {/* Error */}
 {error && (
 <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-xl text-xs font-semibold flex items-center gap-2">
 <span className="text-sm leading-none">⚠️</span>
 <span>{error}</span>
 </div>
 )}

 {/* Email */}
 <div>
 <label htmlFor="email" className="block text-xs font-semibold text-slate-700 mb-1">
 Email address
 </label>
 <input
 id="email"
 name="email"
 type="email"
 required
 autoFocus
 tabIndex={1}
 value={formData.email}
 onChange={handleChange}
 className="appearance-none block w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm"
 placeholder={formData.role === 'university' ? 'admissions@example.edu.pk' : 'you@example.com'}
 />
 </div>

 {/* Password */}
 <div>
 <div className="flex items-center justify-between mb-1">
 <label htmlFor="password" className="block text-xs font-semibold text-slate-700">
 Password
 </label>
 <Link
 to="/forgot-password"
 tabIndex={4}
 className="text-xs font-semibold text-primary-600 hover:text-primary-500 transition-colors"
 >
 Forgot password?
 </Link>
 </div>
 <div className="relative">
 <input
 id="password"
 name="password"
 type={showPassword ? 'text' : 'password'}
 required
 tabIndex={2}
 value={formData.password}
 onChange={handleChange}
 className="appearance-none block w-full px-4 py-2.5 pr-11 bg-white border border-slate-200 rounded-xl placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm"
 placeholder="Enter your password"
 />
 <button
 type="button"
 onClick={() => setShowPassword(!showPassword)}
 tabIndex={5}
 className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors focus:outline-none"
 >
 {showPassword ? (
 <EyeSlashIcon className="h-4 w-4" />
 ) : (
 <EyeIcon className="h-4 w-4" />
 )}
 </button>
 </div>
 </div>

 {/* Submit */}
 <div className="pt-1">
 <button
 id="login-submit"
 type="submit"
 disabled={loading}
 tabIndex={3}
 className={`w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent rounded-xl text-sm font-semibold text-white shadow-md active:scale-[0.98] transition-premium cursor-pointer ${
 loading
 ? 'bg-slate-400 cursor-not-allowed shadow-none'
 : 'bg-gradient-to-r from-primary-600 to-indigo-600 hover:from-primary-700 hover:to-indigo-700 shadow-primary-500/20 hover:shadow-lg hover:shadow-primary-500/25 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
 }`}
 >
 {loading ? (
 <>
 <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
 </svg>
 <span>Signing in...</span>
 </>
 ) : (
 'Sign in'
 )}
 </button>
 </div>
 </form>
 </div>
 </div>
 </div>
 );
};

export default Login;
